<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import type { EntrPhoto } from '@/domain/types';
import { contentImageUrlWithSize } from '@/kiosk-core/net/contentImageUrl';
import { keyedImageRetry } from '@/lib/imageRetry';
import { reportPhotoImageFailure } from '@/telemetry/photoAvailability';
import EntrButton from '@/ui/components/EntrButton.vue';
import EntrChrome from '@/ui/components/EntrChrome.vue';
import EntrIcon from '@/ui/components/EntrIcon.vue';
import { useRailScroll } from '@/ui/components/useRailScroll';
import { usePhotoSelectionController } from './controller';
import type {
  PhotoSelectionScreenEvents,
  PhotoSelectionScreenProps,
} from './types';

const props = defineProps<PhotoSelectionScreenProps>();
const emit = defineEmits<PhotoSelectionScreenEvents>();
// Display tier per rail surface: the wide group cell (~720px) gets 1440px
// (2x DPR); portrait cells (~320px) get 640px. Print quality is untouched —
// the print submit sends image UUIDs and the backend reads the originals.
const railSrc = (photo: EntrPhoto): string =>
  photo.type === 'group'
    ? contentImageUrlWithSize(photo.url, 1440, 90)
    : contentImageUrlWithSize(photo.url, 640, 85);

const controller = usePhotoSelectionController(props, emit);
const { t } = useI18n();

const groupRail = useRailScroll();
const portraitRail = useRailScroll();
// Kill-switch: keep group portraits visible by default when the backend
// returns one, but allow disabling the rail for opening-night fallback.
const GROUP_PHOTOS_ENABLED =
  import.meta.env.VITE_ENTR_GROUP_PHOTOS_ENABLED !== 'false';
const hasGroupPhotos = computed(
  () => GROUP_PHOTOS_ENABLED && props.session.groupPhotos.length > 0
);

interface PhotoRail {
  kind: 'group' | 'portrait';
  label: string;
  summary: string;
  photos: EntrPhoto[];
  revealOrder: number;
  onlyPortraits: boolean;
  scroll: ReturnType<typeof useRailScroll>;
}

const rails = computed((): PhotoRail[] => {
  const portraits: PhotoRail = {
    kind: 'portrait',
    label: t('selection.portraits'),
    summary: selectedWithPrice(
      controller.portraitCount.value,
      controller.portraitUnitPriceLabel.value
    ),
    photos: props.session.portraits,
    revealOrder: 4,
    onlyPortraits: !hasGroupPhotos.value,
    scroll: portraitRail,
  };
  if (!hasGroupPhotos.value) return [portraits];
  const group: PhotoRail = {
    kind: 'group',
    label: t('selection.groupPhoto'),
    summary: selectedWithPrice(
      controller.groupCount.value,
      controller.groupUnitPriceLabel.value
    ),
    photos: props.session.groupPhotos,
    revealOrder: 2,
    onlyPortraits: false,
    scroll: groupRail,
  };
  return [group, portraits];
});
// Bounded retry with cache-bust — see imageRetry.ts. photoRetry.isErrored is
// true only after ALL attempts failed.
function photoImageKey(photo: EntrPhoto): string {
  return `${photo.id}:${photo.url}`;
}

const photoRetry = keyedImageRetry(photoImageKey, railSrc, {
  onExhausted(key, attempts) {
    const photo = [
      ...props.session.groupPhotos,
      ...props.session.portraits,
    ].find((item) => photoImageKey(item) === key);
    if (photo) {
      reportPhotoImageFailure({
        group_id: props.session.group.id,
        image_id: photo.printImageUuid ?? photo.id,
        photo_type: photo.type,
        attempts,
      });
    }
  },
});

const totalLabel = computed(() => {
  if (props.quote.totalSelected === 0) return t('selection.selectedZero');
  if (props.quote.totalSelected === 1) return t('selection.selectedOne');
  return t('selection.selectedMany', { count: props.quote.totalSelected });
});

function selectedWithPrice(count: number, price: string): string {
  return t('selection.selectedWithPrice', { count, price });
}

function selectIfEmpty(photoId: string): void {
  if (controller.quantity(photoId) === 0) controller.increase(photoId);
}
</script>

<template>
  <section class="screen selection-screen" data-testid="screen-select-photos">
    <p class="kicker t-caption reveal" style="--reveal-order: 0">{{ t('selection.subtitle') }}</p>
    <h1 class="group-name t-display reveal" style="--reveal-order: 1">{{ session.group.name }}</h1>

    <template v-for="rail in rails" :key="rail.kind">
      <div
        :class="['section-label', `${rail.kind}-label`, 'reveal', { 'only-portraits': rail.onlyPortraits }]"
        :style="{ '--reveal-order': rail.revealOrder }"
      >
        <span>{{ rail.label }}</span>
        <span>{{ rail.summary }}</span>
      </div>

      <div
        :class="['rail-wrap', `${rail.kind}-wrap`, 'reveal', { 'only-portraits': rail.onlyPortraits }]"
        :style="{ '--reveal-order': rail.revealOrder + 1 }"
      >
        <div
          :ref="rail.scroll.bindRail"
          :class="['rail', `${rail.kind}-rail`, { 'only-portraits': rail.onlyPortraits }]"
          :data-testid="`${rail.kind}-photo-rail`"
        >
        <TransitionGroup name="photo-list" tag="div" class="track">
          <article
            v-for="photo in rail.photos"
            :key="photo.id"
            :class="[
              'photo-cell',
              `${rail.kind}-cell`,
              {
                selected: controller.quantity(photo.id) > 0,
                'image-loaded': photoRetry.isLoaded(photo),
                'image-error': photoRetry.isErrored(photo),
              },
            ]"
            :data-image-error="photoRetry.isErrored(photo) ? 'true' : undefined"
            :data-testid="`photo-${photo.id}`"
            data-photo-image
            :data-group-id="session.group.id"
            :data-image-id="photo.printImageUuid ?? photo.id"
            @click="selectIfEmpty(photo.id)"
          >
            <span class="photo-skeleton" aria-hidden="true" />
            <img
              v-if="photo.url && !photoRetry.isErrored(photo)"
              class="sentry-block"
              :src="photoRetry.src(photo)"
              alt=""
              loading="lazy"
              @load="photoRetry.onLoad(photo)"
              @error="photoRetry.onError(photo)"
            />
            <div v-if="controller.quantity(photo.id) > 0" class="quantity">
              <button type="button" data-testid="qty-decrease" :aria-label="t('common.less')" @click.stop="controller.decrease(photo.id)">
                <EntrIcon name="minus" />
              </button>
              <span>{{ controller.quantity(photo.id) }}</span>
              <button type="button" data-testid="qty-increase" :aria-label="t('common.more')" @click.stop="controller.increase(photo.id)">
                <EntrIcon name="plus" />
              </button>
            </div>
          </article>
        </TransitionGroup>
        </div>
        <button
          v-show="rail.scroll.canScrollLeft.value"
          type="button"
          class="rail-arrow rail-arrow-left"
          :aria-label="t('common.less')"
          :data-testid="`${rail.kind}-rail-prev`"
          @click="rail.scroll.page(-1)"
        >
          <EntrIcon name="chevron-left" />
        </button>
        <button
          v-show="rail.scroll.canScrollRight.value"
          type="button"
          class="rail-arrow rail-arrow-right"
          :aria-label="t('common.more')"
          :data-testid="`${rail.kind}-rail-next`"
          @click="rail.scroll.page(1)"
        >
          <EntrIcon name="chevron-left" />
        </button>
      </div>
    </template>

    <div class="rule reveal" style="--reveal-order: 6" />
    <div class="total reveal" style="--reveal-order: 7">
      <div class="total-copy">
        <span class="t-caption">{{ t('selection.total') }}</span>
        <p>{{ totalLabel }}</p>
        <p
          v-if="controller.isPartialVoucherCoverage.value"
          class="voucher-note voucher-partial"
          data-testid="voucher-partial-note"
        >
          {{ t('selection.voucherPartial', controller.partialCoverageParams.value) }}
        </p>
        <p
          v-else-if="quote.hasVoucherNote"
          class="voucher-note"
          data-testid="voucher-note"
        >
          {{ t('selection.voucherNote') }}
        </p>
      </div>
      <strong class="total-amount">{{ controller.amountLabel.value }}</strong>
    </div>

    <div class="cta reveal" style="--reveal-order: 8">
      <EntrButton
        :disabled="quote.totalSelected === 0"
        data-testid="continue-selection"
        @click="controller.continue"
      >
        {{ t('selection.print') }}
        <template #icon><EntrIcon name="arrow-right" /></template>
      </EntrButton>
    </div>

    <EntrChrome
      :step="2"
      can-go-back
      @back="controller.back"
      @help="controller.help"
    />
  </section>
</template>

<style scoped>
.kicker {
  position: absolute;
  top: 128px;
  left: 0;
  right: 0;
  color: var(--entr-text-soft);
  text-align: center;
  text-transform: none;
}

.group-name {
  position: absolute;
  top: 168px;
  left: 64px;
  right: 64px;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: center;
  white-space: nowrap;
}

.section-label {
  position: absolute;
  left: 64px;
  right: 64px;
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  color: var(--entr-text-soft);
  font-size: 22px;
  line-height: 1;
  letter-spacing: 0;
}

.group-label {
  top: 280px;
}

.portrait-label {
  top: 920px;
}

.portrait-label.only-portraits {
  top: 280px;
}

.section-label span:last-child {
  color: var(--entr-text);
  font-size: 28px;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
}

.rail-wrap {
  position: absolute;
  left: 0;
  right: 0;
}

.rail {
  position: absolute;
  inset: 0;
  overflow-x: auto;
  overflow-y: hidden;
  touch-action: pan-x;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-x: contain;
  scrollbar-width: none;
  scroll-snap-type: x mandatory;
  scroll-padding-left: 64px;
  padding: 0 64px;
}

.rail::-webkit-scrollbar {
  display: none;
}

.track {
  display: flex;
  gap: 24px;
  height: 100%;
}

.group-wrap {
  top: 320px;
  height: 440px;
}

.portrait-wrap {
  top: 960px;
  height: 420px;
}

.portrait-wrap.only-portraits {
  top: 320px;
  height: 760px;
}

/* Scroll arrows: shown (v-show) only when the rail actually overflows. Big
   circular targets pinned at the rail edges over the 64px gutter so they
   never hide a cell. Right arrow reuses chevron-left, flipped. */
.rail-arrow {
  position: absolute;
  top: 50%;
  z-index: 4;
  display: grid;
  place-items: center;
  width: 104px;
  height: 104px;
  color: var(--entr-text);
  background: rgba(15, 12, 40, 0.72);
  border-radius: 999px;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.4);
  transform: translateY(-50%);
  touch-action: manipulation;
}

.rail-arrow:active {
  transform: translateY(-50%) scale(0.94);
}

.rail-arrow-left {
  left: 8px;
}

.rail-arrow-right {
  right: 8px;
}

.rail-arrow :deep(svg) {
  width: 52px;
  height: 52px;
}

.rail-arrow-right :deep(svg) {
  transform: scaleX(-1);
}

.photo-cell {
  position: relative;
  flex: 0 0 auto;
  overflow: hidden;
  border-radius: var(--entr-radius-photo);
  background: var(--entr-ink);
  cursor: pointer;
  touch-action: manipulation;
  outline: 0 solid var(--entr-branding-blue);
  outline-offset: -6px;
  scroll-snap-align: start;
  transition:
    outline-width 150ms ease,
    transform 150ms ease;
}

.photo-cell:active {
  transform: scale(0.98);
}

.photo-cell.selected {
  outline-width: 6px;
  animation: selectpop 200ms ease-out;
}

.group-cell {
  width: 720px;
}

.portrait-cell {
  width: 320px;
}

.portrait-rail.only-portraits .portrait-cell {
  width: 420px;
}

.photo-cell img {
  position: relative;
  z-index: 1;
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: saturate(1.08);
  opacity: 0;
  transition: opacity 220ms var(--entr-ease);
}

.photo-cell.image-loaded img {
  opacity: 1;
}

.photo-skeleton {
  position: absolute;
  inset: 0;
  z-index: 0;
  overflow: hidden;
  border-radius: inherit;
  background:
    linear-gradient(
      135deg,
      rgba(123, 143, 245, 0.18),
      rgba(47, 40, 210, 0.28)
    ),
    var(--entr-ink);
  opacity: 1;
  transition: opacity 220ms var(--entr-ease);
}

.photo-skeleton::before {
  position: absolute;
  top: 0;
  bottom: 0;
  left: -70%;
  width: 70%;
  content: "";
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.28),
    transparent
  );
  transform: skewX(-12deg);
  animation: photo-shimmer 1.45s ease-in-out infinite;
}

.photo-skeleton::after {
  position: absolute;
  inset: 0;
  width: min(46%, 220px);
  height: 54px;
  margin: auto;
  content: "";
  background: url("../../assets/brand/entr-logo.svg") center / contain
    no-repeat;
  opacity: 0;
  transition: opacity 180ms var(--entr-ease);
}

.photo-cell.image-loaded .photo-skeleton {
  opacity: 0;
}

.photo-cell.image-error .photo-skeleton::before {
  animation: none;
  opacity: 0;
}

.photo-cell.image-error .photo-skeleton::after {
  opacity: 0.68;
}

.photo-cell::after {
  position: absolute;
  inset: 0;
  z-index: 2;
  content: "";
  background: transparent;
  pointer-events: none;
}

.photo-cell.selected::after {
  background: rgba(47, 40, 210, 0.18);
}

.photo-list-enter-active {
  transition:
    opacity 240ms ease-out,
    transform 240ms ease-out;
}

.photo-list-enter-from {
  opacity: 0;
  transform: translateY(12px);
}

.quantity {
  position: absolute;
  z-index: 3;
  left: 50%;
  bottom: 24px;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 196px;
  height: 84px;
  padding: 8px;
  border-radius: 999px;
  background: var(--entr-ink);
  border: 6px solid var(--entr-ink);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
}

.quantity button {
  width: 62px;
  height: 62px;
  display: grid;
  place-items: center;
  border-radius: 999px;
  background: var(--entr-branding-blue);
  color: var(--entr-text);
  touch-action: manipulation;
}

.quantity span {
  min-width: 44px;
  color: var(--entr-text);
  font-size: 40px;
  font-weight: 700;
  line-height: 1;
  text-align: center;
}

.rule {
  position: absolute;
  top: 1430px;
  left: 64px;
  right: 64px;
  height: 2px;
  background: var(--entr-line);
}

.total {
  position: absolute;
  top: 1470px;
  left: 64px;
  right: 64px;
  display: grid;
  grid-template-columns: minmax(0, 360px) minmax(0, 1fr);
  column-gap: 40px;
  align-items: start;
}

.total-copy {
  display: grid;
  gap: 18px;
  justify-items: start;
}

.total-copy span {
  color: var(--entr-text-soft);
  text-transform: none;
}

.total-amount {
  justify-self: end;
  color: var(--entr-text);
  font-family: var(--entr-font-body);
  font-size: 96px;
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
}

.total p {
  margin: 0;
  max-width: 360px;
  color: var(--entr-text-soft);
  font-size: 26px;
  line-height: 1.35;
}

.voucher-note {
  color: var(--entr-logo-green) !important;
  font-size: 26px !important;
  line-height: 1.25 !important;
}

.cta {
  position: absolute;
  top: 1668px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
}

@keyframes selectpop {
  0% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.015);
  }

  100% {
    transform: scale(1);
  }
}

@keyframes photo-shimmer {
  0% {
    transform: translateX(0) skewX(-12deg);
  }

  100% {
    transform: translateX(250%) skewX(-12deg);
  }
}
</style>
